import { Breadcrumb } from "@app/shared/components/Breadcrumb";
import type { GradeWithCategories } from "@core/grades/types";
import type { BreadcrumbItem } from "@core/shared/types";
import { cn } from "@design-system/lib/utils";
import { trans } from "@mongez/localization";
import {
  BookOpenIcon,
  FolderOpenIcon,
  GraduationCapIcon,
  SparklesIcon,
} from "lucide-react";
import type { GradeDetailsTheme } from "../../hooks/use-grade-details-page.hook";

type GradeDetailsHeroProps = {
  breadcrumbs: BreadcrumbItem[];
  grade: GradeWithCategories;
  theme: GradeDetailsTheme;
  totalCategories: number;
  totalLessons: number;
};

export function GradeDetailsHero({
  breadcrumbs,
  grade,
  theme,
  totalCategories,
  totalLessons,
}: GradeDetailsHeroProps) {
  return (
    <header className="relative overflow-hidden rounded-[2rem] border border-white/60 bg-white shadow-[0_28px_80px_-48px_rgba(15,23,42,0.5)] dark:border-slate-700/70 dark:bg-slate-900">
      <div
        className={cn(
          "absolute inset-x-0 top-0 h-2 bg-gradient-to-r",
          theme.gradient,
        )}
      />
      <div
        className={cn(
          "absolute -start-16 -top-20 size-64 rounded-full blur-3xl",
          theme.glow,
        )}
      />
      <div
        className={cn(
          "absolute -bottom-28 -end-12 size-72 rounded-full blur-3xl",
          theme.glow,
        )}
      />

      <div className="relative px-5 pb-7 pt-8 sm:px-8 sm:pb-9 lg:px-10">
        <div className="mb-7">
          <Breadcrumb items={breadcrumbs} />
        </div>

        <div className="grid items-center gap-8 lg:grid-cols-[minmax(0,1fr)_280px]">
          <div>
            <div
              className={cn(
                "mb-4 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold sm:text-sm",
                theme.soft,
              )}
            >
              <SparklesIcon className="size-4" />
              {trans("grades.readyToLearn")}
            </div>

            <div className="flex items-start gap-4">
              <div
                className={cn(
                  "hidden size-16 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br text-3xl font-black text-white shadow-xl sm:flex",
                  theme.gradient,
                )}
              >
                {grade.number}
              </div>
              <div className="min-w-0">
                <h1 className="font-heading text-3xl font-black leading-tight text-slate-950 dark:text-white sm:text-4xl lg:text-5xl">
                  {grade.name}
                </h1>
                <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-600 dark:text-slate-300 sm:text-base">
                  {grade.description ||
                    trans("grades.gradeDescription", { grade: grade.name })}
                </p>
              </div>
            </div>

            <div className="mt-7 grid max-w-xl grid-cols-2 gap-3">
              <div className="rounded-2xl border border-slate-200/80 bg-slate-50/80 p-4 backdrop-blur dark:border-slate-700 dark:bg-slate-800/70">
                <BookOpenIcon
                  className={cn("mb-2 size-5", theme.accent)}
                />
                <strong className="block text-2xl font-black text-slate-950 dark:text-white">
                  {totalLessons}
                </strong>
                <span className="text-xs text-slate-500 dark:text-slate-400 sm:text-sm">
                  {trans("grades.totalLessonsTitle")}
                </span>
              </div>
              <div className="rounded-2xl border border-slate-200/80 bg-slate-50/80 p-4 backdrop-blur dark:border-slate-700 dark:bg-slate-800/70">
                <FolderOpenIcon
                  className={cn("mb-2 size-5", theme.accent)}
                />
                <strong className="block text-2xl font-black text-slate-950 dark:text-white">
                  {totalCategories}
                </strong>
                <span className="text-xs text-slate-500 dark:text-slate-400 sm:text-sm">
                  {trans("grades.categories")}
                </span>
              </div>
            </div>
          </div>

          <div className="relative mx-auto hidden aspect-square w-full max-w-[260px] lg:block">
            <div
              className={cn(
                "absolute inset-0 rotate-6 rounded-[2.25rem] bg-gradient-to-br opacity-20",
                theme.gradient,
              )}
            />
            <div className="absolute inset-4 overflow-hidden rounded-[2rem] border border-white/70 bg-white/80 shadow-2xl backdrop-blur dark:border-slate-600 dark:bg-slate-800/80">
              {grade.image?.url ? (
                <img
                  src={grade.image.url}
                  alt={grade.name}
                  className="size-full object-cover"
                />
              ) : (
                <div
                  className={cn(
                    "flex size-full flex-col items-center justify-center bg-gradient-to-br text-white",
                    theme.gradient,
                  )}
                >
                  <GraduationCapIcon className="mb-3 size-16" />
                  <span className="text-7xl font-black">{grade.number}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

